import React, { useEffect, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  TablePagination,
  Tabs,
  Typography,
} from '@mui/material';

import { useGetApprovalRequests } from '~/hooks/apis/loyalty/useGetApprovalRequests';

import type { ClaimCampaign, EvidenceField } from '~/types/claimCampaignTypes';
import type { ApprovalRequestStatus } from '~/types/loyaltyContract';

import RequestsTable from './RequestsTable';

const STATUS_TABS: Array<{ label: string; value: ApprovalRequestStatus | 'ALL' }> = [
  { label: 'Pending', value: 'PENDING' },
  { label: 'Submitted', value: 'SUBMITTED' },
  { label: 'Approved', value: 'APPROVED' },
  { label: 'Rejected', value: 'REJECTED' },
  { label: 'All', value: 'ALL' },
];

const PAGE_SIZE = 25;

interface ApprovalRequestsDialogProps {
  open: boolean;
  campaign: ClaimCampaign | null;
  onClose: () => void;
}

export default function ApprovalRequestsDialog({ open, campaign, onClose }: ApprovalRequestsDialogProps) {
  const [statusTab, setStatusTab] = useState<ApprovalRequestStatus | 'ALL'>('PENDING');
  const [page, setPage] = useState(0);

  useEffect(() => {
    if (open) {
      setStatusTab('PENDING');
      setPage(0);
    }
  }, [open]);

  const statusFilter = statusTab === 'ALL' ? undefined : statusTab;
  const { data, isLoading, error } = useGetApprovalRequests(campaign?.id ?? null, statusFilter, page, PAGE_SIZE);

  const evidenceFields: EvidenceField[] = campaign?.evidenceFields ?? [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Approval Requests
        {campaign && (
          <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
            — {campaign.name}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={statusTab}
            onChange={(_, v) => {
              setStatusTab(v);
              setPage(0);
            }}
          >
            {STATUS_TABS.map((t) => (
              <Tab key={t.value} value={t.value} label={t.label} />
            ))}
          </Tabs>
        </Box>

        <Box p={2}>
          {isLoading && (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          )}

          {error && <Alert severity="error">Failed to load requests: {error.message}</Alert>}

          {!isLoading && !error && data && campaign && (
            <>
              <RequestsTable requests={data.requests} campaignId={campaign.id} evidenceFields={evidenceFields} />
              <TablePagination
                component="div"
                count={data.pagination.total}
                page={page}
                rowsPerPage={PAGE_SIZE}
                rowsPerPageOptions={[PAGE_SIZE]}
                onPageChange={(_, newPage) => setPage(newPage)}
              />
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
